import { useState } from 'react';
import { Button } from '@primereact/ui/button';
import { loadStripe } from '@stripe/stripe-js/pure';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';

import { API_SUBS_CHECKOUT } from '../../common/EsConstants';
import { esPost } from '../../common/EsFetch';
import { EsCheckbox } from '../../common/EsPrime';
import { esHexOf } from '../../common/EsTailwindPalette';
import { esTokenColor } from '../../common/EsChart';
import { FREE_REASONS, esPrice } from '../../common/EsSubs';
import { esLiteral, useEsStrings } from '../../common/EsStrings';

/**
 * A year's subscription by card, through Stripe Embedded Checkout: the button
 * asks esback for a Checkout Session, and its client secret mounts Stripe's
 * own form here. Stripe sends the reader on to VIEW_SUBSCRIBE_DONE.
 *
 * <p>The pure entry point, so Stripe.js loads when somebody presses the button
 * rather than on every page of the site. One Stripe per publishable key, made
 * once: the provider refuses a second instance.
 */
const gStripes = new Map();

const stripeFor = ( pKey ) => {
    if ( !gStripes.has( pKey ) ) gStripes.set( pKey, loadStripe( pKey ) );
    return gStripes.get( pKey );
};

export const paymentsClosed = ( t = esLiteral ) => t( 'subscribe-checkout-closed', 'Payments are not open yet.' );

export const autoRenewLabel = ( t = esLiteral ) => t( 'subscribe-checkout-auto-renew', 'Renew automatically each year' );

// A lifetime account renews nothing; its yearly payment is a donation (Tim, 2026-10-13).
export const autoDonateLabel = ( t = esLiteral ) => t( 'subscribe-checkout-auto-donate', 'Donate automatically each year' );

/**
 * The page's own colors as #rrggbb, read from the live tokens so they follow the Colorsheet
 * and light/dark. Stripe's embedded form is an iframe our CSS cannot reach, so the colors
 * travel with the checkout request and esback sets them as the session's branding. A token
 * that cannot be read is left out and Stripe keeps its default.
 */
export const pageColors = () => {
    const aRoot = document.documentElement;
    const aBackground = esHexOf( esTokenColor( aRoot, 'surface' ) );
    const aButton = esHexOf( esTokenColor( aRoot, 'button-submit' ) );
    return {
        ...( aBackground && { backgroundColor: aBackground } ),
        ...( aButton && { buttonColor: aButton } ),
    };
};

/**
 * plain draws the template's own buttonSubmit rather than a PrimeReact Button,
 * for EsAccountSub, whose controls are all plain.
 *
 * <p>Auto-renew is off unless ticked (Tim, 2026-10-03): off is one payment for
 * a year, on is a yearly Stripe subscription. esback refuses either while the
 * account already auto-renews, and EsAccountSub offers neither then.
 */
export default function EsSubCheckout( { me, label, plain, 'data-testid': dataTestId } ) {
    const t = useEsStrings();
    const [ clientSecret, setClientSecret ] = useState( null );
    const [ busy, setBusy ] = useState( false );
    const [ error, setError ] = useState( null );
    const [ autoRenew, setAutoRenew ] = useState( false );


    const open = Boolean( me?.paymentsEnabled && me?.publishableKey );

    const start = () => {
        setBusy( true );
        setError( null );

        esPost( API_SUBS_CHECKOUT, { autoRenew, ...pageColors() } )
            .then( ( pAnswer ) => setClientSecret( pAnswer?.clientSecret ?? null ) )
            .catch( ( pError ) => setError( pError.status === 503 ? paymentsClosed( t ) : pError.message ) )
            .finally( () => setBusy( false ) );
    };

    const aLabel = busy ? t( 'subscribe-checkout-opening', 'Opening…' )
        : ( label ?? t( 'subscribe-checkout-label', 'Subscribe for {price} a year', { price: esPrice( me?.priceCents ) } ) );

    if ( clientSecret ) {
        return (
            <div className="mt-4" data-testid={ dataTestId + '-stripe' }>
                <EmbeddedCheckoutProvider stripe={ stripeFor( me.publishableKey ) }
                                          options={ { clientSecret } }>
                    <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
            </div>
        );
    }

    return (
        <div className={ plain ? 'mt-6' : 'mt-8 mb-4' }>
            <div className="mb-8 flex items-center gap-2">
                <EsCheckbox id={ dataTestId + '-auto-renew-box' } name="autoRenew" checked={ autoRenew }
                            disabled={ !open || busy } data-testid={ dataTestId + '-auto-renew' }
                            onChange={ ( pEvent ) => setAutoRenew( Boolean( pEvent.target.value ) ) } />
                <label htmlFor={ dataTestId + '-auto-renew-box' } className="text-sm text-body">
                    { FREE_REASONS.includes( me?.reason ) ? autoDonateLabel( t ) : autoRenewLabel( t ) }
                </label>
            </div>
            { plain
                ? <button type="button" data-testid={ dataTestId + '-button' }
                          disabled={ !open || busy } onClick={ start }
                          className="buttonSubmit flex w-full items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                    { aLabel }
                </button>
                : <Button type="button" data-testid={ dataTestId + '-button' }
                          disabled={ !open || busy } onClick={ start }>
                    { aLabel }
                </Button> }
            { !open &&
                <p className="mt-2 text-sm esMuted" data-testid={ dataTestId + '-closed' }>
                    { paymentsClosed( t ) }
                </p> }
            { error &&
                <p className="mt-2 text-sm text-danger" role="alert" data-testid={ dataTestId + '-error' }>
                    { error }
                </p> }
        </div>
    );
}
